import type { NostrEvent } from '@nostrify/nostrify';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FlagSVG, getFlagSVGString } from './FlagSVG';
import { hexToBytes, bytesToFlagData, formatRemainder } from '@/lib/flagUtils';
import { useAuthor } from '@/hooks/useAuthor';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Download, Copy, Check, Trash2, Pencil } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

interface FlagDetailDialogProps {
  event: NostrEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canDelete?: boolean;
  canEdit?: boolean;
  onDelete?: (event: NostrEvent) => void;
  onEdit?: (event: NostrEvent) => void;
}

export function FlagDetailDialog({
  event,
  open,
  onOpenChange,
  canDelete,
  canEdit,
  onDelete,
  onEdit,
}: FlagDetailDialogProps) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const title = event?.tags.find(([n]) => n === 'title')?.[1] ?? 'Untitled';
  const dataHex = event?.tags.find(([n]) => n === 'data')?.[1] ?? '';
  const encoding = event?.tags.find(([n]) => n === 'encoding')?.[1] ?? 'hex';
  const source = event?.tags.find(([n]) => n === 'source')?.[1];

  const author = useAuthor(event?.pubkey);
  const displayName = author.data?.metadata?.name ?? (event?.pubkey.slice(0, 8) + '…');
  const avatar = author.data?.metadata?.picture;
  const npub = event ? nip19.npubEncode(event.pubkey) : '';

  let bytes: number[] = [];
  let flagData = null;
  try {
    bytes = hexToBytes(dataHex);
    flagData = bytesToFlagData(bytes);
  } catch { /* ignore */ }

  const remainderText = flagData ? formatRemainder(flagData.remainder) : '';

  async function copyHex() {
    await navigator.clipboard.writeText(dataHex.toUpperCase().replace(/.{2}/g, '$& ').trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function downloadSVG() {
    if (!bytes.length) return;
    const svg = getFlagSVGString(bytes, 800, 500);
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9]/gi, '_')}.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'SVG downloaded' });
  }

  if (!event) return null;

  // Format hex in groups of 2 bytes with spaces
  const formattedHex = dataHex.toUpperCase().replace(/.{2}/g, '$& ').trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Flag preview */}
          <div className="rounded-lg overflow-hidden border border-border">
            <FlagSVG bytes={bytes} className="w-full" width={800} height={500} />
          </div>

          {/* Author */}
          <div className="flex items-center gap-2">
            <Link
              to={`/${npub}`}
              onClick={() => onOpenChange(false)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              title={`View ${displayName}'s profile flag`}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={avatar} />
                <AvatarFallback>{displayName[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="text-sm text-muted-foreground hover:text-foreground transition-colors">{displayName}</span>
            </Link>
            <Badge variant="outline" className="ml-auto">{encoding}</Badge>
          </div>

          {/* Stripe info */}
          {flagData && (
            <div className="text-sm text-muted-foreground">
              <span>{flagData.stripes.length} stripe{flagData.stripes.length !== 1 ? 's' : ''}</span>
              {flagData.remainder.length > 0 && (
                <span className="ml-2">· Remainder: <code className="font-mono text-foreground">{remainderText}</code></span>
              )}
              <span className="ml-2">· {bytes.length} byte{bytes.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Source text */}
          {source && encoding === 'text' && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Original Text</p>
              <div className="bg-muted rounded-md p-3 text-sm font-mono break-all">{source}</div>
            </div>
          )}

          {/* Hex sequence */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Hex Sequence</p>
            <div className="bg-muted rounded-md p-3 text-sm font-mono break-all leading-relaxed">
              {formattedHex || '—'}
            </div>
          </div>

          {/* Stripe color breakdown */}
          {flagData && flagData.stripes.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Color Breakdown</p>
              <div className="flex flex-wrap gap-2">
                {flagData.stripes.map((stripe, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-muted rounded-md px-2 py-1">
                    <div
                      className="h-4 w-4 rounded-sm border border-border"
                      style={{ background: `rgb(${stripe.r},${stripe.g},${stripe.b})` }}
                    />
                    <span className="text-xs font-mono text-foreground">
                      #{stripe.r.toString(16).padStart(2,'0').toUpperCase()}{stripe.g.toString(16).padStart(2,'0').toUpperCase()}{stripe.b.toString(16).padStart(2,'0').toUpperCase()}
                    </span>
                  </div>
                ))}
                {flagData.remainder.length > 0 && (
                  <div className="flex items-center bg-muted rounded-md px-2 py-1">
                    <span className="text-xs font-mono text-muted-foreground">{remainderText}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={copyHex}>
              {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {copied ? 'Copied!' : 'Copy hex'}
            </Button>
            <Button variant="outline" size="sm" onClick={downloadSVG}>
              <Download className="h-4 w-4 mr-1" />
              Download SVG
            </Button>
            {canEdit && onEdit && (
              <Button variant="outline" size="sm" onClick={() => { onEdit(event); onOpenChange(false); }}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
            {canDelete && onDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => { onDelete(event); onOpenChange(false); }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
